import SearchIcon from "@mui/icons-material/Search";
import { InputAdornment } from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs from "dayjs";
import isEmpty from "lodash/isEmpty";
import React, { FC, ReactElement, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import {
  FormController,
  MuiBox,
  MuiDateField,
  MuiOptionMenu,
  MuiSelect,
  MuiTable,
  MuiTextField,
  MuiTypography,
  OptionMenuItemProps,
  RowProps,
} from "../../../../../../../../components";
import {
  STATUS_BADGE_TYPE,
  StatusBadge,
} from "../../../../../../../../components/statusBadge/StatusBadge";
import { MuiNotesToolTip } from "../../../../../../../../components/tooltip/notesToolTip/MuiNotesToolTip";
import {
  EMPTY_VALUE_DASH,
  MAX_LENGTH_50,
} from "../../../../../../../../helpers/constants";
import { LabelValue } from "../../../../../../../../helpers/types/common";
import { useNavigationSiteCore } from "../../../../../../../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../../../../../../../hooks/useSiteCore";
import { siteCoreContentLoadingSelector } from "../../../../../../../../redux/slices/appState/appStateSelector";
import { ChangeProgramStatus } from "../../../../../../../createProgram/components/createProgramTabs/tabs/program/components/changeProgramStatus/ChangeProgramStatus";
import {
  ProgramStatusList,
  ViewProgramStatusResultKey,
} from "../../../../../../../createProgram/modelsAndSchemas/Models";
import { programSiteCoreKeys } from "../../../../../../../createProgram/siteCore/SiteCoreKeys";
import { ProgramStatusResultWrapper } from "./ProgramStatusResult.style";
import { ViewProgramClosureDetails } from "../../../../../../../createProgram/components/createProgramTabs/tabs/program/components/viewProgramClosureDetails/ViewProgramClosureDetails";
import { useGetLoggedInUserDetails } from "../../../../../../../../aAdminTabTeam2/hooksAndFunctions/customHooks/useGetLoggedInUserDetails";
import { getDateFormatByCountry } from "../../../../../../../../helpers/constants/countryDateFormat";
import {
  compareDates,
  convertToUserFormat,
  getValueOrBlank,
} from "../../../../../../../../helpers/utils";

interface ProgramStatusResultProp {
  rows: ProgramStatusList[];
  pageId: string;
  isEditProgram?: boolean;
  isViewProgram?: boolean;
  refetchProgramStatusList: () => void;
  showEditForReopen?: boolean;
  onRefreshProgramDetail?: (programId: string) => void;
  deliveryCountry?: string;
}

export const ProgramStatusResult: FC<ProgramStatusResultProp> = ({
  rows,
  pageId,
  isEditProgram,
  isViewProgram = false,
  refetchProgramStatusList,
  showEditForReopen = false,
  onRefreshProgramDetail,
  deliveryCountry,
}) => {
  const { id: programId } = useParams();
  const { fetchLabel, commonLabels, fetchValueByKeyOfDropdown } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();
  const tableRef = React.useRef<{ resetPagination: () => void }>(null);
  const [filteredResults, setFilteredResults] = useState([...rows]);
  const [allUpdatedRows, setAllUpdatedRows] = useState<ProgramStatusList[]>([]);
  const [showChangeStatusModal, setShowChangeStatusModal] = useState(false);
  const [selectedProgramStatus, setSelectedProgramStatus] =
    useState<ProgramStatusList>();
  const [uniqueStatusFilter, setUniqueStatusFilter] = useState<LabelValue[]>(
    [],
  );
  const [showViewStatusModal, setShowViewStatusModal] = useState(false);
  const [isEditInactivateProgram, setIsEditInactivateProgram] = useState(false);
  const [isReopenProgram, setIsReopenProgram] = useState(false);

  const siteCoreLoading = useSelector(siteCoreContentLoadingSelector);
  const resetPagination = () => {
    if (tableRef.current) {
      tableRef.current.resetPagination();
    }
  };
  const { loggedInUser } = useGetLoggedInUserDetails();
  const userCountryDateFormat = getDateFormatByCountry(
    getValueOrBlank(loggedInUser?.countryCode),
  );

  useEffect(() => {
    resetPagination();
    reset();
  }, [rows]);

  const getUniqueStatus = (
    data: ProgramStatusList[],
    key: string,
  ): LabelValue[] => {
    const uniqueValues = Array.from(
      new Set(data.map((item) => item[key]).filter(Boolean)), // filter out null/undefined
    );

    return uniqueValues.map((value) => ({
      value: value as string,
      label: value as string,
    }));
  };

  useEffect(() => {
    if (!siteCoreLoading) {
      const localData = structuredClone(rows);

      localData.forEach((data) => {
        data.statusKey = data.status;
        data.status = fetchValueByKeyOfDropdown(
          programSiteCoreKeys.programStatus,
          programSiteCoreKeys.viewProgramStatusList,
          data.status,
        );
      });

      if (localData.length > 0) {
        localData[0].showEditForReopen = showEditForReopen; //show edit button for reopen status
      }

      setAllUpdatedRows(localData);
      setFilteredResults([...localData]);
      setUniqueStatusFilter(
        getUniqueStatus(localData, ViewProgramStatusResultKey.status),
      );
    }
  }, [rows, siteCoreLoading, showEditForReopen]);

  const { control, watch, setValue, getValues, reset } = useForm({
    mode: "all",
    defaultValues: {
      [ViewProgramStatusResultKey.createdTimeStamp]: "",
      [ViewProgramStatusResultKey.status]: "",
      [ViewProgramStatusResultKey.fromDate]: "",
      [ViewProgramStatusResultKey.throughDate]: "",
      [ViewProgramStatusResultKey.createdByName]: "",
    },
  });
  useEffect(() => {
    const { createdTimeStamp, status, fromDate, throughDate, createdByName } =
      getValues();
    if (!isEmpty(allUpdatedRows)) {
      const filteredData = allUpdatedRows.filter((item) => {
        const createdDateMatch = compareDates(
          getValueOrBlank(createdTimeStamp),
          getValueOrBlank(item?.createdTimeStamp),
          getValueOrBlank(userCountryDateFormat),
        );

        const statusMatch =
          status === "" || item.status.toLowerCase() === status.toLowerCase();

        const inActiveFromDateMatch = compareDates(
          getValueOrBlank(fromDate),
          getValueOrBlank(item?.fromDate),
          getValueOrBlank(userCountryDateFormat),
        );

        const inActiveThroughDateMatch = compareDates(
          getValueOrBlank(throughDate),
          getValueOrBlank(item?.throughDate),
          getValueOrBlank(userCountryDateFormat),
        );

        const createdNameMatch =
          !createdByName ||
          item?.createdByName
            .toLowerCase()
            .includes(createdByName.toLowerCase());

        return (
          createdDateMatch &&
          statusMatch &&
          inActiveFromDateMatch &&
          inActiveThroughDateMatch &&
          createdNameMatch
        );
      });
      setFilteredResults([...filteredData]);
      resetPagination();
    }
  }, [
    watch(ViewProgramStatusResultKey.createdTimeStamp),
    watch(ViewProgramStatusResultKey.status),
    watch(ViewProgramStatusResultKey.fromDate),
    watch(ViewProgramStatusResultKey.throughDate),
    watch(ViewProgramStatusResultKey.createdByName),
  ]);

  const handleUpdateReopenClick = (row: ProgramStatusList) => {
    setShowChangeStatusModal(true);
    setSelectedProgramStatus(row);
    setIsEditInactivateProgram(false);
    setIsReopenProgram(true);
  };

  const handleUpdateClick = (row: ProgramStatusList) => {
    setShowChangeStatusModal(true);
    setSelectedProgramStatus(row);
    setIsEditInactivateProgram(true);
    setIsReopenProgram(false);
  };

  const handleViewClick = (row: ProgramStatusList) => {
    setShowViewStatusModal(true);
    setSelectedProgramStatus(row);
  };

  const programStatusActionColumn = {
    id: "action",
    label: fetchLabel(
      programSiteCoreKeys.programStatus,
      programSiteCoreKeys.action,
    ),
    className: "cell-actions",
    render: (row: RowProps) => {
      const menuItems: OptionMenuItemProps[] = [];

      if (isEditProgram && row?.showEditForReopen) {
        menuItems.push({
          onClick: () => handleUpdateReopenClick(row as ProgramStatusList),
          dataTestId: `edit`,
          label: commonLabels.edit,
          ariaLabel: commonLabels.edit,
        });
      }

      if (isEditProgram && row?.statusKey === STATUS_BADGE_TYPE.inActive) {
        menuItems.push({
          onClick: () => handleUpdateClick(row as ProgramStatusList),
          dataTestId: `edit`,
          label: commonLabels.edit,
          ariaLabel: commonLabels.edit,
        });
      }

      if (row?.statusKey === STATUS_BADGE_TYPE.closed) {
        menuItems.push({
          onClick: () => handleViewClick(row as ProgramStatusList),
          dataTestId: `view`,
          label: commonLabels.view,
          ariaLabel: commonLabels.view,
        });
      }

      return menuItems.length ? <MuiOptionMenu menuItems={menuItems} /> : null;
    },
  };

  const columns = [
    {
      id: ViewProgramStatusResultKey.createdTimeStamp,
      label: fetchLabel(
        programSiteCoreKeys.programStatus,
        programSiteCoreKeys.createdDate,
      ),
      classNames: "cell-created-date",
      render: (row: any) => (
        <MuiBox>
          <MuiTypography variant="h3" className="">
            {convertToUserFormat(row?.createdTimeStamp, userCountryDateFormat)}
          </MuiTypography>
        </MuiBox>
      ),
      isSortVisible: true,
    },
    {
      id: ViewProgramStatusResultKey.status,
      label: fetchLabel(
        programSiteCoreKeys.programStatus,
        programSiteCoreKeys.changeProgramStatus,
      ),
      isSortVisible: true,
      classNames: "cell-status",
      render: (row: any) => (
        <StatusBadge status={row.statusKey} label={row.status} />
      ),
    },
    {
      id: ViewProgramStatusResultKey.fromDate,
      label: fetchLabel(
        programSiteCoreKeys.programStatus,
        programSiteCoreKeys.programStatusFromDate,
      ),
      isSortVisible: true,
      classNames: "cell-form-date",
      isEmptyValue: true,
      render: (row: any) => (
        <MuiBox>
          <MuiTypography variant="h3" className="">
            {!isEmpty(row.fromDate)
              ? convertToUserFormat(row.fromDate, userCountryDateFormat)
              : EMPTY_VALUE_DASH}
          </MuiTypography>
        </MuiBox>
      ),
    },
    {
      id: ViewProgramStatusResultKey.throughDate,
      label: fetchLabel(
        programSiteCoreKeys.programStatus,
        programSiteCoreKeys.programStatusThroughDate,
      ),
      isSortVisible: true,
      isEmptyValue: true,
      classNames: "cell-through-date",
      render: (row: any) => (
        <MuiBox>
          <MuiTypography variant="h3" className="">
            {!isEmpty(row.throughDate)
              ? convertToUserFormat(row.throughDate, userCountryDateFormat)
              : EMPTY_VALUE_DASH}
          </MuiTypography>
        </MuiBox>
      ),
    },
    {
      id: ViewProgramStatusResultKey.createdByName,
      label: fetchLabel(
        programSiteCoreKeys.programStatus,
        programSiteCoreKeys.createdName,
      ),
      isSortVisible: true,
      classNames: "cell-createdby",
      render: (row: any) => (
        <MuiBox>
          <MuiTypography variant="h3" className="">
            {!isEmpty(row?.createdByName)
              ? row?.createdByName
              : EMPTY_VALUE_DASH}
          </MuiTypography>
        </MuiBox>
      ),
    },
    {
      id: "notes",
      label: commonLabels.notes,
      isSortVisible: true,
      classNames: "cell-notes",
      render: (row: any) => (
        <MuiBox>
          <MuiNotesToolTip message={row?.notes} />
        </MuiBox>
      ),
    },
    ...(isEditProgram || isViewProgram ? [programStatusActionColumn] : []),
  ];

  const tableHeaderForm = [
    <FormController
      key="createdDate"
      control={control}
      name={ViewProgramStatusResultKey.createdTimeStamp}
    >
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <MuiDateField
          format={userCountryDateFormat}
          valueFormat={userCountryDateFormat}
          name="createdDate"
          clearable
          value={
            getValues(ViewProgramStatusResultKey.createdTimeStamp) !== ""
              ? dayjs(
                  getValues(ViewProgramStatusResultKey.createdTimeStamp),
                  userCountryDateFormat,
                )
              : null
          }
          onChange={(newValue) => {
            setValue(
              ViewProgramStatusResultKey.createdTimeStamp,
              newValue?.format(userCountryDateFormat) || "",
            );
          }}
          requiredARIALabel={commonARIALabel.required}
          selectDateARIALabel={commonARIALabel.selectDate}
        />
      </LocalizationProvider>
    </FormController>,
    <FormController
      key="status"
      control={control}
      name={ViewProgramStatusResultKey.status}
    >
      <MuiSelect
        label=""
        placeholder={commonLabels.selectPlaceHolder}
        options={uniqueStatusFilter}
        visibleDefaultSelectOption
        value=""
        id={`${pageId}-${ViewProgramStatusResultKey.status}`}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,

    <FormController
      key="fromDate"
      control={control}
      name={ViewProgramStatusResultKey.fromDate}
    >
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <MuiDateField
          valueFormat={userCountryDateFormat}
          format={userCountryDateFormat}
          name={ViewProgramStatusResultKey.fromDate}
          clearable
          value={
            getValues(ViewProgramStatusResultKey.fromDate) !== ""
              ? dayjs(
                  getValues(ViewProgramStatusResultKey.fromDate),
                  userCountryDateFormat,
                )
              : null
          }
          onChange={(newValue) => {
            setValue(
              ViewProgramStatusResultKey.fromDate,
              newValue?.format(userCountryDateFormat) || "",
            );
          }}
          requiredARIALabel={commonARIALabel.required}
          selectDateARIALabel={commonARIALabel.selectDate}
        />
      </LocalizationProvider>
    </FormController>,
    <FormController
      key="throughDate"
      control={control}
      name={ViewProgramStatusResultKey.throughDate}
    >
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <MuiDateField
          valueFormat={userCountryDateFormat}
          format={userCountryDateFormat}
          name={ViewProgramStatusResultKey.throughDate}
          clearable
          value={
            getValues(ViewProgramStatusResultKey.throughDate) !== ""
              ? dayjs(
                  getValues(ViewProgramStatusResultKey.throughDate),
                  userCountryDateFormat,
                )
              : null
          }
          onChange={(newValue) => {
            setValue(
              ViewProgramStatusResultKey.throughDate,
              newValue?.format(userCountryDateFormat) || "",
            );
          }}
          requiredARIALabel={commonARIALabel.required}
          selectDateARIALabel={commonARIALabel.selectDate}
        />
      </LocalizationProvider>
    </FormController>,
    <FormController
      key="createdByName"
      control={control}
      name={ViewProgramStatusResultKey.createdByName}
    >
      <MuiTextField
        data-testid="filter-created-by"
        id={`${pageId}-${ViewProgramStatusResultKey.createdByName}`}
        startAdornment={
          <InputAdornment position="start">
            <SearchIcon />
          </InputAdornment>
        }
        aria-label={commonLabels.search}
        maxLength={MAX_LENGTH_50}
        placeholder={commonLabels.search}
        showCloseIcon
        shouldTrimSpaces={false}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <></>,
    isEditProgram ? <></> : null,
  ];

  return (
    <ProgramStatusResultWrapper>
      <MuiTable
        ref={tableRef}
        pageId={pageId}
        tableHeaderForm={tableHeaderForm as ReactElement[]}
        columns={columns}
        tableResults={[...filteredResults]}
        noDataInApi={true}
        priorityResults={[]}
        noResultMessage={commonLabels.searchCriteria}
        isLastColumnSticky={isEditProgram}
      />
      {showChangeStatusModal && (
        <ChangeProgramStatus
          pageId={pageId}
          open={showChangeStatusModal}
          setShowChangeStatusModal={setShowChangeStatusModal}
          programId={programId ?? ""}
          isEditInactivateProgram={isEditInactivateProgram}
          inActiveStatusData={selectedProgramStatus}
          refetchProgramStatusList={refetchProgramStatusList}
          showEditForReopen={isReopenProgram}
          onRefreshProgramDetail={onRefreshProgramDetail}
        />
      )}

      {showViewStatusModal && (
        <ViewProgramClosureDetails
          pageId={pageId}
          open={showViewStatusModal}
          setShowModal={setShowViewStatusModal}
          programId={programId ?? ""}
          statusId={selectedProgramStatus?.id ?? ""}
          deliveryCountry={deliveryCountry}
        />
      )}
    </ProgramStatusResultWrapper>
  );
};
